import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PartnerMaintenanceConflictError } from '~/app/services/partners/partners.service';
import type {
  ConflictingPartnerScopeItem,
  MaintenanceContractIssue,
  MaintenancePartnershipValidity,
  MaintenanceRulesStatus,
  MaintenanceSlaContractRecord,
  MaintenanceSlaDraft,
  MaintenanceSlaDraftLevel,
  MaintenanceSlaDraftScopeRule,
  MaintenanceSlaLevelCode,
  MaintenanceSlaScopeHierarchyAsset,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  collectMaintenanceContractIssues,
  createEmptyMaintenanceSlaDraft,
  createEmptyScopeRule,
  createFixedLevelsForInterventionMode,
  isMaintenanceIssueVisible,
  maintenanceDraftToRecord,
  maintenanceRecordToDraft,
  propagateLevelValues,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';

type Args = {
  open: boolean;
  relationshipId: string;
  partnership: MaintenancePartnershipValidity | null;
  saved: MaintenanceSlaContractRecord | null;
  onSave: (
    record: MaintenanceSlaContractRecord,
    rulesStatus: MaintenanceRulesStatus,
  ) => void | Promise<void>;
  scopeAssetHierarchy?: MaintenanceSlaScopeHierarchyAsset[];
};

export function useMaintenanceSlaContract({
  open,
  relationshipId,
  partnership,
  saved,
  onSave,
  scopeAssetHierarchy = [],
}: Args) {
  const [draft, setDraft] = useState<MaintenanceSlaDraft | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflictingScopeOverlaps, setConflictingScopeOverlaps] = useState<
    ConflictingPartnerScopeItem[]
  >([]);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const lastSavedByRelationshipRef = useRef<
    Map<string, MaintenanceSlaContractRecord>
  >(new Map());
  const nextSessionKey = open ? relationshipId : null;

  if (nextSessionKey !== sessionKey) {
    setSessionKey(nextSessionKey);
    setShowErrors(false);
    setConflictingScopeOverlaps([]);
    if (open) {
      const cached = lastSavedByRelationshipRef.current.get(relationshipId);
      const effective = saved ?? cached ?? null;
      setDraft(
        effective
          ? maintenanceRecordToDraft(effective)
          : createEmptyMaintenanceSlaDraft(),
      );
    } else {
      setDraft(null);
    }
  }

  const issues = useMemo(
    () =>
      draft ? collectMaintenanceContractIssues(draft, scopeAssetHierarchy) : [],
    [draft, scopeAssetHierarchy],
  );

  const visibleIssues = useMemo(
    () =>
      issues.filter((issue) =>
        isMaintenanceIssueVisible(issue.code, showErrors),
      ),
    [issues, showErrors],
  );

  const updateDraft = useCallback((patch: Partial<MaintenanceSlaDraft>) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      const merged: MaintenanceSlaDraft = { ...current, ...patch };

      if (
        'interventionMode' in patch &&
        patch.interventionMode !== undefined &&
        patch.interventionMode !== current.interventionMode
      ) {
        const newMode = patch.interventionMode;
        const baseLevels = createFixedLevelsForInterventionMode(newMode);
        merged.levels = baseLevels.map((baseLevel) => {
          const existing = current.levels.find(
            (l) => l.code === baseLevel.code,
          );
          return existing ?? baseLevel;
        });

        if (current.interventionMode === 'BOTH') {
          merged.scopeRules = current.scopeRules.map((rule) => ({
            ...rule,
            interventionMode: null,
          }));
        }
      }

      return merged;
    });
  }, []);

  useEffect(() => {
    if (!draft) {
      return;
    }
    collectMaintenanceContractIssues(draft, scopeAssetHierarchy);
  }, [draft, scopeAssetHierarchy]);

  const updateLevel = useCallback(
    (code: MaintenanceSlaLevelCode, level: MaintenanceSlaDraftLevel) => {
      setDraft((current) => {
        if (!current) {
          return current;
        }

        const updatedLevels = current.levels.map((item) =>
          item.code === code ? level : item,
        );

        return {
          ...current,
          levels: propagateLevelValues(updatedLevels, level),
        };
      });
    },
    [],
  );

  const addScopeRule = useCallback(() => {
    setDraft((current) =>
      current
        ? {
            ...current,
            scopeRules: [...current.scopeRules, createEmptyScopeRule()],
          }
        : current,
    );
  }, []);

  const updateScopeRule = useCallback(
    (ruleId: string, patch: Partial<MaintenanceSlaDraftScopeRule>) => {
      setDraft((current) =>
        current
          ? {
              ...current,
              scopeRules: current.scopeRules.map((rule) =>
                rule.id === ruleId ? { ...rule, ...patch } : rule,
              ),
            }
          : current,
      );
    },
    [],
  );

  const removeScopeRule = useCallback((ruleId: string) => {
    setDraft((current) =>
      current
        ? {
            ...current,
            scopeRules: current.scopeRules.filter((rule) => rule.id !== ruleId),
          }
        : current,
    );
  }, []);

  const cancel = useCallback(() => {
    setDraft(null);
    setShowErrors(false);
    setConflictingScopeOverlaps([]);
  }, []);

  const clearConflicts = useCallback(() => {
    setConflictingScopeOverlaps([]);
  }, []);

  const saveWithStatus = useCallback(
    async (rulesStatus: MaintenanceRulesStatus) => {
      if (!draft || saving) {
        return;
      }

      if (!partnership) {
        return;
      }

      const draftIssues = collectMaintenanceContractIssues(
        draft,
        scopeAssetHierarchy,
      );
      if (rulesStatus === 'PUBLISHED' && draftIssues.length > 0) {
        setShowErrors(true);
        return;
      }

      setConflictingScopeOverlaps([]);
      setSaving(true);
      try {
        const savedRecord = maintenanceDraftToRecord(
          relationshipId,
          draft,
          partnership,
          rulesStatus,
        );
        await onSave(savedRecord, rulesStatus);
        lastSavedByRelationshipRef.current.set(relationshipId, savedRecord);
        setDraft(null);
        setShowErrors(false);
        setConflictingScopeOverlaps([]);
      } catch (err) {
        if (err instanceof PartnerMaintenanceConflictError) {
          setConflictingScopeOverlaps(err.conflicts);
        } else {
          throw err;
        }
      } finally {
        setSaving(false);
      }
    },
    [draft, onSave, partnership, relationshipId, saving],
  );

  const saveDraft = useCallback(
    () => saveWithStatus('DRAFT'),
    [saveWithStatus],
  );

  const publish = useCallback(
    () => saveWithStatus('PUBLISHED'),
    [saveWithStatus],
  );

  const mode = draft ? 'edit' : saved ? 'read' : 'empty';

  return {
    mode,
    draft,
    saving,
    showErrors,
    issues,
    visibleIssues,
    conflictingScopeOverlaps,
    clearConflicts,
    cancel,
    publish,
    saveDraft,
    updateDraft,
    updateLevel,
    addScopeRule,
    updateScopeRule,
    removeScopeRule,
  };
}

export type MaintenanceSlaContractState = ReturnType<
  typeof useMaintenanceSlaContract
>;

export type { MaintenanceContractIssue };
